import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ClipLoader from 'react-spinners/ClipLoader';

/**
 * Customer ID shown as text with a pencil that swaps it for an input in place.
 * `onSave` receives the trimmed value and may return a promise.
 * `children` render alongside the value and drop away while editing.
 */
function EditableCustomerId({
  value, onSave, className = '', inputClassName = '', placeholder = '—', iconSize = '16px',
  valueClassName = 'text-surface-500 text-sm', children = null, editorAlign = 'left',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef(null);
  // disabling the input while saving blurs it, which would re-enter commit()
  // through onBlur and fire a second PATCH
  const savingRef = useRef(false);

  // resync when the drawer/row switches to a different connection
  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const stopEditing = () => {
    setDraft(value || '');
    setFailed(false);
    setEditing(false);
  };

  const commit = () => {
    if (savingRef.current) return;
    const trimmed = draft.trim();
    // an empty box means "leave it alone" rather than "clear it", matching the
    // prompt() flow this replaced
    if (!trimmed || trimmed === (value || '')) {
      stopEditing();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setFailed(false);
    Promise.resolve(onSave(trimmed))
      // a failed save discards the edit, so this note is its only trace; it
      // clears itself rather than lingering over a value that never changed
      .catch(() => {
        setFailed(true);
        setTimeout(() => setFailed(false), 3000);
      })
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
        setEditing(false);
      });
  };

  const handleKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') commit();
    if (event.key === 'Escape') stopEditing();
  };

  // the editor is a floating panel rather than an inline swap: the read-only row
  // below keeps its space, so opening the editor never reflows what sits under it
  const editor = (
    <div
      className={`absolute top-1/2 -translate-y-1/2 z-20 flex items-center gap-1
        bg-surface-0 border border-surface-200 rounded-lg shadow-md p-1
        ${editorAlign === 'right' ? 'right-0' : 'left-0'}`}
    >
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={saving}
        aria-label="Customer ID"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        onClick={(event) => event.stopPropagation()}
        className={`input py-1 px-2 text-sm ${inputClassName}`}
      />
      {saving ? <ClipLoader size={12} color="#78716C" /> : (
        <>
          <Tooltip title="Save" arrow placement="top">
            <IconButton
              size="small"
              // onBlur would otherwise commit before the click lands
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                commit();
              }}
              className="text-surface-400 hover:text-success-dark hover:bg-success-light"
              sx={{ padding: '4px' }}
            >
              <CheckIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel" arrow placement="top">
            <IconButton
              size="small"
              // keeps focus so blur does not save the edit being cancelled
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                stopEditing();
              }}
              className="text-surface-400 hover:text-error-dark hover:bg-error-light"
              sx={{ padding: '4px' }}
            >
              <CloseIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </div>
  );

  return (
    <div className={`relative flex items-center gap-1 min-w-0 ${className}`}>
      {/* kept in flow while editing (just hidden) so the row holds its height */}
      <div className={`flex items-center gap-1 min-w-0 ${editing ? 'invisible' : ''}`}>
        <span className={`truncate ${valueClassName}`}>{value || placeholder}</span>
        {failed && (
          <span role="alert" className="text-xs text-error-dark whitespace-nowrap">
            Couldn&apos;t save
          </span>
        )}
        <Tooltip title="Edit Customer ID" arrow placement="top">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setEditing(true);
            }}
            className="shrink-0 text-surface-400 hover:text-surface-600 hover:bg-surface-100"
            sx={{ padding: '4px' }}
          >
            <EditNoteIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>
        {children}
      </div>
      {editing && editor}
    </div>
  );
}

export default EditableCustomerId;
